import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, MinusCircle, PauseCircle, RefreshCw, XCircle } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { workflowsService, WorkflowRun, WorkflowRunStatus, WorkflowRunStep, WorkflowTrigger } from '@/services/workflows.service';
import { ACTION_LABELS, TRIGGER_LABELS } from './workflowMeta';

const STATUS: Record<WorkflowRunStatus, { label: string; tone: string }> = {
  succeeded:   { label: 'Succeeded', tone: 'bg-success-soft text-success' },
  failed:      { label: 'Failed', tone: 'bg-danger-soft text-danger' },
  waiting:     { label: 'Waiting', tone: 'bg-info-soft text-info' },
  running:     { label: 'Running', tone: 'bg-primary-soft text-primary' },
  interrupted: { label: 'Interrupted', tone: 'bg-warning-soft text-warning' },
};

const STEP_ICON: Record<WorkflowRunStep['status'], ReactNode> = {
  succeeded: <CheckCircle2 size={13} className="text-success" />,
  failed: <XCircle size={13} className="text-danger" />,
  skipped: <MinusCircle size={13} className="text-text-muted" />,
  waiting: <PauseCircle size={13} className="text-info" />,
  stopped: <AlertTriangle size={13} className="text-warning" />,
};

const FILTERS: Array<{ value: '' | WorkflowRunStatus; label: string }> = [
  { value: '', label: 'All' }, { value: 'failed', label: 'Failed' }, { value: 'waiting', label: 'Waiting' },
  { value: 'succeeded', label: 'Succeeded' }, { value: 'interrupted', label: 'Interrupted' },
];

interface Props { ruleUid?: string; ruleName?: string; onClose: () => void }

export function WorkflowRunLog({ ruleUid, ruleName, onClose }: Props) {
  const [status, setStatus] = useState<'' | WorkflowRunStatus>('');
  const [runs, setRuns] = useState<WorkflowRun[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (before?: string) => {
    setLoading(true);
    try {
      const page = await workflowsService.runs({ ruleUid, status: status || undefined, limit: 25, before });
      setRuns(prev => (before && prev ? [...prev, ...page.runs] : page.runs));
      setHasMore(page.hasMore);
    } catch {
      setRuns(prev => prev ?? []);
    } finally { setLoading(false); }
  }, [ruleUid, status]);

  useEffect(() => { setRuns(null); load(); }, [load]);

  return (
    <Modal open onClose={onClose} title={ruleName ? `Run log — ${ruleName}` : 'Workflow run log'} widthClass="max-w-3xl">
      <div className="space-y-3 px-6 py-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(f => (
              <button key={f.value} type="button" onClick={() => setStatus(f.value)}
                className={clsx('rounded-full px-2.5 py-1 text-2xs font-medium transition-colors', status === f.value ? 'bg-primary text-white' : 'bg-surface-alt text-text-secondary hover:bg-surface-alt/70')}>
                {f.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="ghost" iconLeft={<RefreshCw size={12} />} onClick={() => load()} loading={loading && !runs}>Refresh</Button>
        </div>
        <p className="text-2xs text-text-muted">Steps never re-run automatically. Runs are kept for 180 days.</p>

        {runs === null && <div className="skeleton h-24 rounded-lg" />}
        {runs && runs.length === 0 && <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-xs text-text-muted">No runs yet.</p>}
        {runs && runs.length > 0 && (
          <div className="divide-y divide-border rounded-xl border border-border">
            {runs.map(run => {
              const expanded = open === run.runUid;
              return (
                <div key={run.runUid}>
                  <button type="button" onClick={() => setOpen(expanded ? null : run.runUid)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-alt/60">
                    {expanded ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
                    <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-2xs font-semibold', STATUS[run.status]?.tone ?? 'bg-surface-alt text-text-muted')}>{STATUS[run.status]?.label ?? run.status}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-text-primary">{!ruleUid && run.ruleName ? `${run.ruleName} · ` : ''}{run.subject ?? 'Event'}</span>
                      <span className="block truncate text-2xs text-text-muted">{TRIGGER_LABELS[run.trigger as WorkflowTrigger] ?? run.trigger}</span>
                    </span>
                    <span className="shrink-0 text-right text-2xs text-text-muted">
                      {new Date(run.createdAt).toLocaleString()}
                      {run.status === 'waiting' && run.waitingUntil && <span className="flex items-center justify-end gap-1 text-info"><Clock size={10} /> until {new Date(run.waitingUntil).toLocaleString()}</span>}
                    </span>
                  </button>
                  {expanded && (
                    <div className="space-y-1.5 bg-surface-alt/30 px-4 pb-3 pt-1">
                      {run.leadUid && <Link to={`/leads/${run.leadUid}`} className="text-2xs text-primary hover:underline">Open lead</Link>}
                      {!run.leadUid && run.dealUid && <Link to={`/deals?deal=${run.dealUid}`} className="text-2xs text-primary hover:underline">Open deal</Link>}
                      {run.steps.length === 0 && <p className="text-2xs text-text-muted">No steps recorded.</p>}
                      {run.steps.map((step, index) => (
                        <div key={index} className="flex items-start gap-2 text-2xs">
                          <span className="mt-0.5">{STEP_ICON[step.status]}</span>
                          <span className="min-w-0 flex-1">
                            <span className="font-medium text-text-primary">{ACTION_LABELS[step.kind as keyof typeof ACTION_LABELS] ?? step.kind}</span>
                            {step.message && <span className="text-text-muted"> — {step.message}</span>}
                          </span>
                          <span className="shrink-0 text-text-muted">{new Date(step.at).toLocaleTimeString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {hasMore && runs && (
          <div className="flex justify-center">
            <Button size="sm" variant="secondary" loading={loading} onClick={() => load(runs[runs.length - 1]?.createdAt)}>Load older runs</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
